import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import Raven from 'raven-js';
import { useNavigationComponent } from 'nest/application/hooks';
import { chatspotSidebarComponentDef } from 'navigation-components/config';
import { useIsCopilotOpen } from 'copilot-toolkit/hooks/useIsCopilotOpen';

// `hide`/`show` on `chatspotSidebarComponentDef` is Breeze's actual
// open/close, not a visibility wrapper — never call `show` on Academy close,
// it reopens Breeze outright.
export const useBreezeAssistantExclusivity = ({
  isAcademyOpen,
  isFullscreen,
  onBreezeOpen
}) => {
  const {
    hide,
    show
  } = useNavigationComponent({
    componentDef: chatspotSidebarComponentDef
  });
  const isBreezeOpen = useIsCopilotOpen();
  const [prevBreezeOpen, setPrevBreezeOpen] = useState(isBreezeOpen);
  const [isConfirmingBreezeSwitch, setIsConfirmingBreezeSwitch] = useState(false);
  const hideBreeze = useCallback(() => hide().catch(Raven.captureException.bind(Raven)), [hide]);
  if (prevBreezeOpen !== isBreezeOpen) {
    setPrevBreezeOpen(isBreezeOpen);
    if (!prevBreezeOpen && isBreezeOpen && isAcademyOpen && isFullscreen) {
      setIsConfirmingBreezeSwitch(true);
    }
  }
  useLayoutEffect(() => {
    if (isAcademyOpen) {
      void hideBreeze();
    }
  }, [isAcademyOpen, hideBreeze]);
  useLayoutEffect(() => {
    if (isConfirmingBreezeSwitch) {
      void hideBreeze();
    }
  }, [isConfirmingBreezeSwitch, hideBreeze]);
  const wasBreezeOpenRef = useRef(isBreezeOpen);
  useLayoutEffect(() => {
    const breezeJustOpened = !wasBreezeOpenRef.current && isBreezeOpen;
    wasBreezeOpenRef.current = isBreezeOpen;
    if (breezeJustOpened && isAcademyOpen && !isFullscreen) {
      onBreezeOpen();
    }
  }, [isBreezeOpen, isAcademyOpen, isFullscreen, onBreezeOpen]);
  const confirmBreezeSwitch = useCallback(() => {
    setIsConfirmingBreezeSwitch(false);
    onBreezeOpen();
    show().catch(Raven.captureException.bind(Raven));
  }, [onBreezeOpen, show]);
  const cancelBreezeSwitch = useCallback(() => {
    setIsConfirmingBreezeSwitch(false);
  }, []);
  return {
    isConfirmingBreezeSwitch,
    confirmBreezeSwitch,
    cancelBreezeSwitch
  };
};